import type { ApiError } from '@alvo/types';

export interface RequestConfig extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
}

declare global {
  interface ImportMeta {
    env: {
      VITE_API_URL?: string;
      [key: string]: string | undefined;
    };
  }
}

const BASE_URL = import.meta.env?.VITE_API_URL || '/api/v1';


// Error event dispatcher for centralized error handling
function dispatchErrorEvent(type: string, detail: any) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(`api-error-${type}`, { detail }));
  }
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[2]) : null;
}

// Token storage in memory + localStorage backup
let accessTokenMemory: string | null = null;
let refreshTokenMemory: string | null = null;
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function onAccessTokenRefreshed(token: string) {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
}

function addRefreshSubscriber(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

class HttpClient {
  private baseUrl: string;
  private defaultHeaders: Record<string, string>;

  constructor(baseUrl: string = BASE_URL) {
    this.baseUrl = baseUrl;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
  }

  /**
   * Fetch a fresh CSRF cookie from Sanctum.
   * Called before every write request — the endpoint is a 204 No Content (cheap).
   * This eliminates stale/expired token issues permanently.
   */
  private csrfCachedAt = 0;
  private async freshCsrf(): Promise<string> {
    // Cache CSRF cookie for 5 minutes to avoid extra requests on every save
    const now = Date.now();
    const existing = getCookie('XSRF-TOKEN');
    if (existing && (now - this.csrfCachedAt) < 300000) {
      return existing;
    }
    await fetch('/sanctum/csrf-cookie', { credentials: 'include' });
    this.csrfCachedAt = now;
    const token = getCookie('XSRF-TOKEN');
    if (!token) {
      throw new Error('CSRF token not found after /sanctum/csrf-cookie fetch');
    }
    return token;
  }

  private getHeaders(xsrf?: string, extra?: HeadersInit): Record<string, string> {
    const headers: Record<string, string> = { ...this.defaultHeaders };
    if (xsrf) {
      headers['X-XSRF-TOKEN'] = xsrf;
    }
    if (extra) {
      Object.assign(headers, extra);
    }
    return headers;
  }

  private buildUrl(endpoint: string, params?: Record<string, string | number | boolean | undefined>): string {
    const url = new URL(`${this.baseUrl}${endpoint}`, window.location.origin);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.append(key, String(value));
        }
      });
    }
    return url.toString();
  }

  private async handleResponse<T>(response: Response, retryRequest?: () => Promise<Response>, retryCount: number = 0): Promise<T> {
    // Handle 429 with exponential backoff (max 3 retries)
    if (response.status === 429 && retryCount < 3) {
      const retryAfter = parseInt(response.headers.get('Retry-After') || '1', 10);
      const backoffMs = Math.min(retryAfter * 1000, 2 ** retryCount * 1000); // Exponential backoff capped by Retry-After
      
      await new Promise(resolve => setTimeout(resolve, backoffMs));
      
      if (retryRequest) {
        const retryResponse = await retryRequest();
        return this.handleResponse<T>(retryResponse, retryRequest, retryCount + 1);
      }
    }

    // Handle 401 with token refresh
    if (response.status === 401 && retryRequest) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newAccessToken = await this.refreshAccessToken();
          if (newAccessToken) {
            isRefreshing = false;
            onAccessTokenRefreshed(newAccessToken);
            // Retry the original request with new token
            const retryResponse = await retryRequest();
            return this.handleResponse<T>(retryResponse);
          } else {
            // Refresh failed - redirect to login
            isRefreshing = false;
            this.clearTokens();
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
            throw new Error('Session expired. Please login again.');
          }
        } catch (error) {
          isRefreshing = false;
          this.clearTokens();
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          throw error;
        }
      } else {
        // Another request is already refreshing - wait for it
        return new Promise((resolve, reject) => {
          addRefreshSubscriber(async (token) => {
            try {
              const retryResponse = await retryRequest();
              resolve(this.handleResponse<T>(retryResponse));
            } catch (error) {
              reject(error);
            }
          });
        });
      }
    }

    if (!response.ok) {
      const error: ApiError = {
        message: response.statusText || 'An error occurred',
        status: response.status
      };
      try {
        const errorData = await response.json();
        error.message = errorData.message || error.message;
        error.code = errorData.code;
        (error as any).error = errorData.error;
        (error as any).ip = errorData.ip;
        (error as any).response = errorData;
        error.details = errorData.details;
      } catch {
        // Response body wasn't JSON
      }
            if (response.status === 403) {
        dispatchErrorEvent('forbidden', error);
      } else if (response.status === 419) {
        dispatchErrorEvent('session-expired', error);
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      } else if (response.status === 500) {
        dispatchErrorEvent('server-error', error);
      }
      
      throw error;
    }
    if (response.status === 204) {
      return {} as T;
    }
    return response.json();
  }

  private async refreshAccessToken(): Promise<string | null> {
    const refreshToken = refreshTokenMemory || localStorage.getItem('alvo-refresh-token');
    if (!refreshToken) {
      return null;
    }

    try {
      const response = await fetch(`${this.baseUrl}/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ refresh_token: refreshToken })
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      if (data.data?.token && data.data?.refresh_token) {
        this.setAuthToken(data.data.token, data.data.refresh_token);
        return data.data.token;
      }
      return null;
    } catch (error) {
      console.error('Token refresh failed:', error);
      dispatchErrorEvent('network-error', { message: 'Failed to refresh token' });
      return null;
    }
  }

  async get<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    const { params, ...requestConfig } = config || {};
    const url = this.buildUrl(endpoint, params);
    const xsrf = getCookie('XSRF-TOKEN') || '';
    
    const makeRequest = async () => {
      return fetch(url, {
        ...requestConfig,
        method: 'GET',
        credentials: 'include',
        headers: this.getHeaders(xsrf, requestConfig.headers)
      });
    };

    const response = await makeRequest();
    return this.handleResponse<T>(response, makeRequest, 0);
  }

  async post<T>(endpoint: string, data?: unknown, config?: RequestConfig): Promise<T> {
    return this.writeRequest<T>('POST', endpoint, data, config);
  }

  async put<T>(endpoint: string, data?: unknown, config?: RequestConfig): Promise<T> {
    return this.writeRequest<T>('PUT', endpoint, data, config);
  }

  async patch<T>(endpoint: string, data?: unknown, config?: RequestConfig): Promise<T> {
    return this.writeRequest<T>('PATCH', endpoint, data, config);
  }

  async delete<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.writeRequest<T>('DELETE', endpoint, undefined, config);
  }

  /**
   * Unified write request handler.
   * Always fetches a fresh CSRF token before sending.
   * No caching, no stale tokens, no 419 surprises.
   */
  private async writeRequest<T>(method: string, endpoint: string, data?: unknown, config?: RequestConfig): Promise<T> {
    const { params, ...requestConfig } = config || {};
    const url = this.buildUrl(endpoint, params);

    // If we have a Bearer token, use pure token auth (no CSRF, no cookies)
    // If no token, use SPA mode (CSRF + session cookies)
    // Fetch fresh CSRF token for SPA cookie auth
    let xsrf = "";
    try {
      xsrf = await this.freshCsrf();
    } catch (e) {
      console.warn('CSRF refresh failed, using existing cookie:', e);
      xsrf = getCookie('XSRF-TOKEN') || '';
    }
    const headers = this.getHeaders(xsrf, requestConfig.headers);

    // For FormData (file uploads), skip JSON.stringify and let browser set Content-Type with boundary
    const isFormData = typeof FormData !== "undefined" && data instanceof FormData;
    if (isFormData) {
      delete headers["Content-Type"];
    }

    const makeRequest = async () => {
      return fetch(url, {
        ...requestConfig,
        method,
        credentials: 'include',
        headers,
        body: data ? (isFormData ? data as BodyInit : JSON.stringify(data)) : undefined
      });
    };

    const response = await makeRequest();
    return this.handleResponse<T>(response, makeRequest, 0);
  }

  setAuthToken(accessToken: string | null, refreshToken?: string | null) {
    if (accessToken) {
      accessTokenMemory = accessToken;
      this.defaultHeaders['Authorization'] = `Bearer ${accessToken}`;
      localStorage.setItem("alvo-token", accessToken); // Backup for page refresh
      
      if (refreshToken) {
        refreshTokenMemory = refreshToken;
        localStorage.setItem("alvo-refresh-token", refreshToken);
      }
    } else {
      this.clearTokens();
    }
  }

  clearTokens() {
    accessTokenMemory = null;
    refreshTokenMemory = null;
    delete this.defaultHeaders['Authorization'];
    localStorage.removeItem("alvo-token");
    localStorage.removeItem("alvo-refresh-token");
    // Clear persisted auth state to prevent infinite 401 redirect loops
    localStorage.removeItem("alvo-auth");
  }
}

export const httpClient = new HttpClient();

// Restore tokens from localStorage on module load (before any component mounts)
try { 
  const accessToken = localStorage.getItem("alvo-token");
  const refreshToken = localStorage.getItem("alvo-refresh-token");
  if (accessToken) {
    httpClient.setAuthToken(accessToken, refreshToken);
  }
} catch {}

export default httpClient;
